import { Grid, Typography } from '@material-ui/core';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { DscrCalculatorStore } from '../stores/dscrCalculatorStore';
import {
  CalculateButton,
  CalculatorLayout,
  LoanAutocomplete,
  PaperList,
  PaperListItem,
  PaperListTitle,
} from './dscrCalculatorComponents';
import {
  AmortizingPeriodMonths,
  AnnualHOA,
  CreditLoss,
  EstimatedAsIsValue,
  GrossPotentialRent,
  InPlaceMonthlyRent,
  Insurance,
  InterestOnlyPeriodMonths,
  LeasingAndMarketing,
  LoanAmount,
  LoanSubtype,
  Miscellaneous,
  MonthlyMarketRent,
  NoteInterestRate,
  PropertyManagementFee,
  RepairsAndMaintenance,
  ReplacementReserves,
  RequestedLTV,
  Taxes,
  TurnoverCosts,
  UnitCount,
  Utilities,
  Vacancy,
} from './dscrCalculatorFields';

interface DscrCalcSectionProps {
  store: DscrCalculatorStore;
}

export const DscrCalculatorPortfolio = ({ store }: DscrCalcSectionProps) => {
  return (
    <CalculatorLayout
      left={
        <>
          <Grid item xs={12}>
            <LoanAutocomplete store={store} />
          </Grid>
          <CalculatorInputs store={store} />
          <CalculateButton onClick={() => store.fetchCalculatedValues()} />
        </>
      }
      right={<CalculatedDSCRValues store={store} />}
    />
  );
};

const CalculatorInputs = observer(({ store }: DscrCalcSectionProps) => {
  const {
    vacancyPercentOfGPR,
    propertyManagementFeePercentOfGPR,
  } = store.form.fields;

  const defaults = store.defaultValues?.[store.form.fields.loanSubtype.value];

  return (
    <>
      <Grid item xs={12}>
        <Typography variant={'h5'}>Loan Values</Typography>
      </Grid>
      <LoanSubtype store={store} />
      <EstimatedAsIsValue store={store} />
      <NoteInterestRate store={store} />
      <RequestedLTV store={store} />
      <InterestOnlyPeriodMonths store={store} />
      <AmortizingPeriodMonths store={store} />
      <LoanAmount store={store} />

      <Grid item xs={12}>
        <Typography variant={'h5'}>Income</Typography>
      </Grid>
      <InPlaceMonthlyRent store={store} />
      <MonthlyMarketRent store={store} />
      <GrossPotentialRent store={store} />
      <Vacancy
        store={store}
        helperText={`* Vacancy is greater of in-place and  ${formatPercentage(
          vacancyPercentOfGPR.value
        )}`}
      />
      <CreditLoss
        store={store}
        helperText={`* Include tenants under forbearance`}
      />

      <Grid item xs={12}>
        <Typography variant={'h5'}>Expenses</Typography>
      </Grid>
      <Taxes store={store} helperText={`* Based on current tax year`} />
      <Insurance store={store} helperText={`* Based on in-place at closing`} />
      <AnnualHOA store={store} />
    </>
  );
});

const CalculatedDSCRValues = observer(({ store }: DscrCalcSectionProps) => {
  const values = store.calculatedValues;
  const { fields } = store.form;

  const formatCurr = value =>
    value || value === 0 ? formatCurrency(value) : '';
  const formatPerc = value =>
    value || value === 0 ? formatPercentage(value) : '';
  return (
    <>
      <Grid item xs={12}>
        <PaperList>
          <PaperListTitle text="Income" />
          <PaperListItem
            label="Gross Annual Income"
            value={formatCurr(fields.grossAnnualIncome.value || 0)}
          />
        </PaperList>
      </Grid>
      <Grid item xs={12}>
        <PaperList>
          <PaperListTitle text="Expenses" />
          <PaperListItem
            label="Total Operating Expenses"
            value={formatCurr(store.operatingExpenses)}
          />
        </PaperList>
      </Grid>
      <Grid item xs={12}>
        <PaperList>
          <PaperListTitle text="Economics" />
          <PaperListItem
            label="IO Period Debt Service"
            value={formatCurr(values?.debtServiceInIOPeriod)}
          />
          <PaperListItem
            label="IO Period PITIA DSCR"
            value={values?.pitiaDscrInIOPeriod ?? ""}
          />
          <PaperListItem
            label="Fully Amortizing Debt Service"
            value={formatCurr(values?.fullyAmortizingDebtService)}
          />
          <PaperListItem
            label="Fully Amortizing PITIA DSCR"
            value={values?.fullyAmortizingDscrInIOPeriod ?? ""}
          />
        </PaperList>
      </Grid>
    </>
  );
});
